import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faCalendarDays, faUserClock } from '@fortawesome/free-solid-svg-icons';
import LiveSchedule from './LiveSchedule';
import PaidAvailability from './PaidAvailability';
import './MySchedule.css';

const API = process.env.REACT_APP_API_BASE_URL;

const TABS = [
  { key: 'schedule', label: 'Live Schedule', icon: faCalendarDays },
  { key: 'paid', label: 'Demo & Paid Availability', icon: faUserClock },
];

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* The clock lives in its own component, so only it re-renders every
   few seconds — not the whole schedule underneath. */
const Clock = ({ timeZone, offsetLabel, updating }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="rmtsch-clock">
      <div className="rmtsch-clock-now">
        <strong>{now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</strong>
        <span>{now.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
      </div>
      <span className="rmtsch-clock-tz">
        <FontAwesomeIcon icon={faGlobe} /> {timeZone.replace(/_/g, ' ')} ({offsetLabel})
        {updating && <em>Saving…</em>}
      </span>
    </div>
  );
};

const MySchedule = ({ userData }) => {
  // Tab is kept in the address (?tab=paid) so other pages can link straight to it
  const [params, setParams] = useSearchParams();
  const currentTab = params.get('tab') === 'paid' ? 'paid' : 'schedule';
  const [updatingTz, setUpdatingTz] = useState(false);

  const teacherId = userData?.id;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMin = -new Date().getTimezoneOffset();
  const offsetLabel = `UTC${offsetMin >= 0 ? '+' : '-'}${String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, '0')}:${String(
    Math.abs(offsetMin) % 60
  ).padStart(2, '0')}`;

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'schedule') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Save the teacher's time zone (same logic + endpoints) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    (async () => {
      setUpdatingTz(true);
      try {
        const offsetHours = -new Date().getTimezoneOffset() / 60;
        const res = await axios.get(`${API}/api/teacher/myclasses/OffsetList`, authHeader());
        let list = res.data;
        if (!Array.isArray(list) && list && typeof list === 'object') {
          list =
            (Array.isArray(list.data) && list.data) ||
            (Array.isArray(list.recordset) && list.recordset) ||
            (Array.isArray(list.result) && list.result) ||
            Object.values(list).filter((item) => item && typeof item === 'object' && item.Id !== undefined);
        }
        if (!Array.isArray(list)) list = [];

        let settingId = list.find((s) => s.Timezone === timeZone)?.Id ?? null;
        if (!settingId) {
          const added = await axios.post(`${API}/api/teacher/myclasses/addTimezone`, { timezone: timeZone, offsetHours }, authHeader());
          settingId = added.data.id;
        }
        if (settingId) {
          await axios.put(`${API}/api/teacher/myclasses/updateOffset/${teacherId}`, { offsetId: settingId }, authHeader());
        }
      } catch (error) {
        console.error('Error detecting/updating teacher timezone:', error);
      } finally {
        if (!cancelled) setUpdatingTz(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, timeZone]);

  if (!userData) {
    return (
      <div className="rmtsch" aria-busy="true">
        <div className="rmtsch-inner">
          <div className="rmtsch-skel rmtsch-skel--title" />
          <div className="rmtsch-skel rmtsch-skel--bar" />
          <div className="rmtsch-skel rmtsch-skel--body" />
        </div>
      </div>
    );
  }

  const firstName = (userData.fullName || '').trim().split(' ')[0];

  return (
    <div className="rmtsch">
      <div className="rmtsch-inner">
        <header className="rmtsch-head">
          <div>
            <span className="rmtsch-eyebrow">My Schedule</span>
            <h1 className="rmtsch-title">
              {firstName ? (
                <>
                  Hello, <span>{firstName}</span>
                </>
              ) : (
                <>
                  Your <span>schedule</span>
                </>
              )}
            </h1>
          </div>
          <Clock timeZone={timeZone} offsetLabel={offsetLabel} updating={updatingTz} />
        </header>

        <div className="rmtsch-tabs" role="tablist" aria-label="Schedule sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`rmtsch-tab-${t.key}`}
              aria-selected={currentTab === t.key}
              aria-controls="rmtsch-panel"
              className={`rmtsch-tab ${currentTab === t.key ? 'is-on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <FontAwesomeIcon icon={t.icon} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        <div id="rmtsch-panel" role="tabpanel" aria-labelledby={`rmtsch-tab-${currentTab}`} className="rmtsch-panel">
          {currentTab === 'schedule' && <LiveSchedule userData={userData} />}
          {currentTab === 'paid' && <PaidAvailability userData={userData} />}
        </div>
      </div>
    </div>
  );
};

export default MySchedule;